import React, { useEffect, useState } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  TablePagination,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Stack,
  Chip,
  Grid,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import api from '../../services/api';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import PrintIcon from '@mui/icons-material/Print';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { useNavigate } from 'react-router-dom';
import PDFViewer from '../shared/PDFViewer';
import AplicarSaldoReciboDialog from './AplicarSaldoReciboDialog';
import VerAsientoContableButton from '../shared/VerAsientoContableButton';

interface IOrdenPago {
  nroFormulario: number;
  fechaEmision: string;
  nroProveedor: number;
  nombre: string;
  importeTotal: number;
  formaDePago: string;
  usuario: string;
  cae_Nro: string;
  cae_Vencimiento: string;
  tipoComprobante: string;
  nroSuc: number;
  descripcion: string;
  cuit: string;
  letra?: string;
  nroComprobante?: number;
  nroAsiento?: number | null;
}

interface OrdenesPagoResponse {
  items: IOrdenPago[];
  totalCount: number;
}

const OrdenPago: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();

  // Obtener el primer y último día del mes actual
  const getFirstDayOfMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  };

  const getLastDayOfMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0);
  };

  const [ordenesPago, setOrdenesPago] = useState<IOrdenPago[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [fechaDesde, setFechaDesde] = useState<string>(() => {
    const fecha = getFirstDayOfMonth();
    return fecha.toISOString().split('T')[0];
  });
  const [fechaHasta, setFechaHasta] = useState<string>(() => {
    const fecha = getLastDayOfMonth();
    return fecha.toISOString().split('T')[0];
  });
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [selectedOrdenPago, setSelectedOrdenPago] = useState<number | null>(null);
  const [aplicarDialogOpen, setAplicarDialogOpen] = useState(false);
  const [aplicarDialogOrden, setAplicarDialogOrden] = useState<IOrdenPago | null>(null);

  // Función para convertir fecha de YYYY-MM-DD a DD-MM-YYYY
  const formatFechaParaAPI = (fecha: string): string => {
    if (!fecha) return '';
    const [year, month, day] = fecha.split('-');
    return `${day}-${month}-${year}`;
  };

  const coerceNroAsiento = (raw: unknown): number | null => {
    if (raw == null || raw === '') return null;
    const n = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  };

  const fetchOrdenesPago = async (pageNumber: number, pageSize: number, search: string, desde: string, hasta: string) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        enqueueSnackbar('No hay token de autenticación. Por favor, inicie sesión nuevamente.', { 
          variant: 'error',
          autoHideDuration: 3000
        });
        return;
      }

      const params: any = {
        pageNumber: pageNumber + 1,
        pageSize,
        searchTerm: search || null
      };

      if (desde) {
        params.FechaDesde = formatFechaParaAPI(desde);
      }
      if (hasta) {
        params.FechaHasta = formatFechaParaAPI(hasta);
      }

      const response = await api.get(
        '/api/Tesoreria/ordendepago',
        {
          params
        }
      );

      const payload = response.data as OrdenesPagoResponse | IOrdenPago[] | Record<string, unknown> | undefined;

      // Manejar diferentes formatos de respuesta (items / Items, con o sin paginación)
      let items: IOrdenPago[] = [];
      let totalCount = 0;

      const mapRow = (item: any): IOrdenPago => ({
        nroFormulario: item.nroFormulario || item.NroFormulario || 0,
        fechaEmision: item.fechaEmision || item.FechaEmision || '',
        nroProveedor: item.nroProveedor || item.NroCliente || item.nroCliente || 0,
        nombre: item.nombre || item.Nombre || '',
        importeTotal: item.importeTotal ?? item.ImporteTotal ?? 0,
        formaDePago: item.formaDePago || item.FormaDePago || '',
        usuario: item.usuario || item.Usuario || '',
        cae_Nro: item.cae_Nro || item.caE_Nro || item.CAE_Nro || '',
        cae_Vencimiento: item.cae_Vencimiento || item.caE_Vencimiento || item.CAE_Vencimiento || '',
        tipoComprobante: item.tipoComprobante || item.nroTipoFormulario || item.NroTipoFormulario || '',
        nroSuc: item.nroSuc ?? item.NroSuc ?? 0,
        descripcion: item.descripcion || item.Descripcion || '',
        cuit: item.cuit || item.CUIT || '',
        letra: item.letra || item.Letra || '',
        nroComprobante: item.nroComprobante ?? item.NroComprobante ?? 0,
        nroAsiento: coerceNroAsiento(item.nroAsiento ?? item.NroAsiento)
      });

      if (Array.isArray(payload)) {
        items = payload.map(mapRow);
        totalCount = items.length;
      } else if (payload && typeof payload === 'object') {
        const arr =
          Array.isArray((payload as OrdenesPagoResponse).items)
            ? (payload as OrdenesPagoResponse).items
            : Array.isArray((payload as { Items?: IOrdenPago[] }).Items)
              ? (payload as { Items: IOrdenPago[] }).Items
              : [];
        items = arr.map(mapRow);
        totalCount =
          (payload as OrdenesPagoResponse).totalCount ??
          (payload as { TotalCount?: number }).TotalCount ??
          items.length;
      }

      setOrdenesPago(items);
      setTotalRecords(totalCount);
    } catch (error: any) {
      const status = error?.response?.status;
      
      if (status === 404 || status === 204) {
        console.log('No hay órdenes de pago disponibles');
        setOrdenesPago([]);
        setTotalRecords(0);
      } else {
        console.error('Error detallado:', error);
        const errorMessage = error.response?.data?.message || 
                           error.response?.data || 
                           'Error al cargar las órdenes de pago';
        enqueueSnackbar(errorMessage, { 
          variant: 'error',
          autoHideDuration: 3000
        });
        setOrdenesPago([]);
        setTotalRecords(0);
      }
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchOrdenesPago(page, rowsPerPage, searchTerm, fechaDesde, fechaHasta);
  }, [page, rowsPerPage, searchTerm, fechaDesde, fechaHasta]);


  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Mantener siempre 10 filas por página
    const newRowsPerPage = parseInt(event.target.value, 10);
    if (newRowsPerPage !== 10) {
      // Si intentan cambiar, mantener en 10
      setRowsPerPage(10);
    } else {
      setRowsPerPage(10);
    }
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleNuevaOrdenPago = () => {
    navigate('/tesoreria/orden-pago/nuevo');
  };

  const handleImprimirPDF = async (nroFormulario: number) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        enqueueSnackbar('No hay token de autenticación', { variant: 'error' });
        return;
      }

      const response = await api.get(`/api/Tesoreria/ordendepago/${nroFormulario}/pdf`, {
        responseType: 'blob',
      });

      const blob = response.data as Blob;
      const url = window.URL.createObjectURL(blob);
      setPdfUrl(url);
      setSelectedOrdenPago(nroFormulario);
      enqueueSnackbar('PDF generado correctamente', { variant: 'success' });
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message
        || error?.message
        || 'Error al generar el PDF de la orden de pago';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const handleClosePDF = () => {
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
    }
    setPdfUrl(null);
    setSelectedOrdenPago(null);
  };

  const handleAbrirAplicarSaldo = (orden: IOrdenPago) => {
    setAplicarDialogOrden(orden);
    setAplicarDialogOpen(true);
  };

  const handleCloseAplicarDialog = () => {
    setAplicarDialogOpen(false);
    setAplicarDialogOrden(null);
  };

  // Función para obtener el chip de estado según el CAE
  const getEstadoChip = (caeNro?: string) => {
    if (caeNro) {
      return {
        label: 'Con CAE',
        bgColor: '#4caf50',
        textColor: '#ffffff'
      };
    }
    return {
      label: 'Sin CAE',
      bgColor: '#ff9800',
      textColor: '#ffffff'
    };
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 2
    }).format(value);
  };

  const formatComprobante = (ordenPago: IOrdenPago) => {
    const { letra, nroSuc, nroComprobante } = ordenPago;
    if (!letra || nroSuc === undefined || nroComprobante === undefined || nroSuc === null || nroComprobante === null) {
      return '-';
    }

    const formatPart = (value: number | string, length: number) => {
      if (value === null || value === undefined) {
        return ''.padStart(length, '0');
      }
      const numeric = Number(value);
      if (!Number.isNaN(numeric)) {
        return numeric.toString().padStart(length, '0');
      }
      return String(value).padStart(length, '0');
    };

    const sucursal = formatPart(nroSuc, 5);
    const comprobante = formatPart(nroComprobante, 8);
    const letraNormalizada = letra.trim().toUpperCase();

    if (!letraNormalizada) {
      return '-';
    }

    return `${letraNormalizada}-${sucursal}-${comprobante}`;
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Tarjeta blanca contenedora principal */}
      <Card 
        sx={{ 
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {/* Header con título y botones */}
          <Stack 
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between" 
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Typography 
              variant={isSmallMobile ? "h5" : "h4"} 
              sx={{ 
                fontWeight: 600,
                color: '#1976d2',
                fontSize: { xs: '1.5rem', sm: '2rem' }
              }}
            >
              Orden de Pago
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleNuevaOrdenPago}
              fullWidth={isSmallMobile}
              sx={{
                borderRadius: 2,
                px: { xs: 2, sm: 3 },
                py: { xs: 1.5, sm: 1 },
                textTransform: 'none',
                fontWeight: 500,
                boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                fontSize: { xs: '0.875rem', sm: '1rem' },
                '&:hover': {
                  boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)',
                }
              }}
            >
              {isSmallMobile ? 'Nueva' : 'Nueva Orden de Pago'}
            </Button>
          </Stack>

          {/* Campo de búsqueda y filtros */}
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Buscar por número, descripción, nombre o CUIT..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#ffffff',
                      }
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#666' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Fecha Desde"
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => {
                    setFechaDesde(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#ffffff',
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Fecha Hasta"
                  type="date"
                  value={fechaHasta}
                  onChange={(e) => {
                    setFechaHasta(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#ffffff',
                      }
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Tabla o Cards según el tamaño de pantalla */}
          {isMobile ? (
            <Stack spacing={2}>
              {ordenesPago.map((ordenPago) => {
                return (
                  <Card 
                    key={ordenPago.nroFormulario} 
                    sx={{ 
                      width: '100%',
                      borderRadius: 2,
                      border: '1px solid #e0e0e0',
                      '&:hover': {
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                      },
                    }}
                  >
                    <CardContent>
                      <Stack spacing={1.5}>
                        <Stack direction="row" justifyContent="space-between" alignItems="start">
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              Orden de Pago #{ordenPago.nroFormulario}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {ordenPago.nombre}
                            </Typography>
                          </Box>
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Fecha:</strong> {new Date(ordenPago.fechaEmision).toLocaleDateString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Proveedor:</strong> {ordenPago.nroProveedor}
                        </Typography>
                        {ordenPago.cuit && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>CUIT:</strong> {ordenPago.cuit}
                          </Typography>
                        )}
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                          <strong>Importe:</strong> {formatCurrency(ordenPago.importeTotal)}
                        </Typography>
                        {ordenPago.usuario && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Usuario:</strong> {ordenPago.usuario}
                          </Typography>
                        )}
                        {ordenPago.cae_Nro && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>CAE:</strong> {ordenPago.cae_Nro}
                          </Typography>
                        )}
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                          <VerAsientoContableButton iconOnly nroAsiento={ordenPago.nroAsiento} />
                          <Tooltip title="Imprimir PDF">
                            <IconButton
                              onClick={() => handleImprimirPDF(ordenPago.nroFormulario)}
                              size="small"
                              sx={{
                                bgcolor: '#2196f3',
                                color: '#fff',
                                borderRadius: 1.5,
                                '&:hover': { bgcolor: '#1976d2' }
                              }}
                            >
                              <PrintIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Aplicar saldo / Ver aplicaciones">
                            <IconButton
                              onClick={() => handleAbrirAplicarSaldo(ordenPago)}
                              size="small"
                              sx={{
                                bgcolor: '#2e7d32',
                                color: '#fff',
                                borderRadius: 1.5,
                                '&:hover': { bgcolor: '#1b5e20' }
                              }}
                            >
                              <AccountBalanceWalletIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
              {loading ? (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center', 
                  justifyContent: 'center',
                  py: 6,
                  px: 2
                }}>
                  <Typography 
                    variant="body1" 
                    color="text.secondary"
                    sx={{ 
                      textAlign: 'center',
                      fontWeight: 500
                    }}
                  >
                    Cargando órdenes de pago...
                  </Typography>
                </Box>
              ) : ordenesPago.length === 0 && (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center', 
                  justifyContent: 'center',
                  py: 6,
                  px: 2
                }}>
                  <Typography 
                    variant="body1" 
                    color="text.secondary"
                    sx={{ 
                      textAlign: 'center',
                      mb: 1,
                      fontWeight: 500
                    }}
                  >
                    Aún no hay órdenes de pago cargadas
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ 
                      textAlign: 'center',
                      opacity: 0.7
                    }}
                  >
                    Las órdenes de pago aparecerán aquí cuando estén disponibles
                  </Typography>
                </Box>
              )}
              <TablePagination
                rowsPerPageOptions={[10]}
                component="div"
                count={totalRecords}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
              />
            </Stack>
          ) : (
            <TableContainer 
              component={Paper}
              sx={{
                borderRadius: 2,
                boxShadow: 'none',
                border: '1px solid #e0e0e0',
              }}
            >
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Nro. Orden Pago</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', md: 'table-cell' } }}>Fecha</TableCell>
                    <TableCell sx={{ fontWeight: 600, display: { xs: 'none', lg: 'table-cell' }, minWidth: 110, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Comprobante</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', sm: 'table-cell' } }}>Proveedor</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Nombre</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', md: 'table-cell' } }}>CUIT</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }} align="right">Importe</TableCell>
                    <TableCell sx={{ fontWeight: 600, display: 'none' }}>Forma de Pago</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', lg: 'table-cell' } }}>Usuario</TableCell>
                    <TableCell sx={{ fontWeight: 600, display: 'none' }}>Estado</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }} align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={isMobile ? 5 : 11} align="center" sx={{ py: 4 }}>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 1, fontWeight: 500, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          Cargando órdenes de pago...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : ordenesPago.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isMobile ? 5 : 11} align="center" sx={{ py: 4 }}>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 1, fontWeight: 500, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          No se encontraron órdenes de pago con los filtros aplicados
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.7, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          Intenta ajustar los filtros de búsqueda o fechas
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    ordenesPago.map((ordenPago) => {
                      const estadoChip = getEstadoChip(ordenPago.cae_Nro);
                      const fechaEmisionDate = new Date(ordenPago.fechaEmision);
                      return (
                        <TableRow 
                          key={ordenPago.nroFormulario}
                          sx={{
                            '&:hover': {
                              bgcolor: '#f9f9f9',
                            },
                          }}
                        >
                          <TableCell sx={{ fontWeight: 500, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{ordenPago.nroFormulario}</TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', md: 'table-cell' } }}>{fechaEmisionDate.toLocaleDateString()}</TableCell>
                          <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                            {formatComprobante(ordenPago)}
                          </TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', sm: 'table-cell' } }}>{ordenPago.nroProveedor}</TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, maxWidth: { xs: 120, sm: 'none' }, overflow: 'hidden', textOverflow: 'ellipsis' }}>{ordenPago.nombre}</TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', md: 'table-cell' } }}>{ordenPago.cuit || '-'}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, color: '#1976d2', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                            {formatCurrency(ordenPago.importeTotal)}
                          </TableCell>
                          <TableCell sx={{ display: 'none' }}>{ordenPago.formaDePago || '-'}</TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', lg: 'table-cell' } }}>{ordenPago.usuario || '-'}</TableCell>
                          <TableCell sx={{ display: 'none' }}>
                            <Chip
                              label={estadoChip.label}
                              size="small"
                              sx={{
                                bgcolor: estadoChip.bgColor,
                                color: estadoChip.textColor,
                                fontWeight: 500,
                                borderRadius: 1.5,
                                minWidth: 90,
                              }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="wrap" useFlexGap>
                              <VerAsientoContableButton iconOnly nroAsiento={ordenPago.nroAsiento} />
                              <Tooltip title="Imprimir PDF">
                                <IconButton
                                  onClick={() => handleImprimirPDF(ordenPago.nroFormulario)}
                                  size="small"
                                  sx={{
                                    bgcolor: '#2196f3',
                                    color: '#fff',
                                    borderRadius: 1.5,
                                    width: { xs: 28, sm: 32 },
                                    height: { xs: 28, sm: 32 },
                                    '&:hover': {
                                      bgcolor: '#1976d2',
                                      transform: 'scale(1.1)',
                                    },
                                    transition: 'all 0.2s'
                                  }}
                                >
                                  <PrintIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Aplicar saldo / Ver aplicaciones">
                                <IconButton
                                  onClick={() => handleAbrirAplicarSaldo(ordenPago)}
                                  size="small"
                                  sx={{
                                    bgcolor: '#2e7d32',
                                    color: '#fff',
                                    borderRadius: 1.5,
                                    width: { xs: 28, sm: 32 },
                                    height: { xs: 28, sm: 32 },
                                    '&:hover': {
                                      bgcolor: '#1b5e20',
                                      transform: 'scale(1.1)',
                                    },
                                    transition: 'all 0.2s'
                                  }}
                                >
                                  <AccountBalanceWalletIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[10]}
                component="div"
                count={totalRecords}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
                sx={{
                  borderTop: '1px solid #e0e0e0',
                }}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {pdfUrl && selectedOrdenPago && (
        <PDFViewer 
          pdfUrl={pdfUrl}
          onClose={handleClosePDF}
          title={`Orden de Pago ${selectedOrdenPago}`}
          documentId={`${selectedOrdenPago}`}
          documentType="ordenPago"
        />
      )}

      {aplicarDialogOrden && (
        <AplicarSaldoReciboDialog
          open={aplicarDialogOpen}
          onClose={handleCloseAplicarDialog}
          reciboId={aplicarDialogOrden.nroFormulario}
          nroCliente={aplicarDialogOrden.nroProveedor}
          nombreCliente={aplicarDialogOrden.nombre || `Proveedor ${aplicarDialogOrden.nroProveedor}`}
          variant="proveedor"
          onSuccess={() => fetchOrdenesPago(page, rowsPerPage, searchTerm, fechaDesde, fechaHasta)}
        />
      )}
    </Box>
  );
};

export default OrdenPago;
